import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DefaultRootState, useSelector } from 'react-redux';
import { NavLinkProps, useLocation } from 'react-router-dom';

import { useBowser } from 'common/hooks/bowser/useBowser';
import { useCustomer } from 'common/hooks/useCustomer/useCustomer.hook';
import { useI18n } from 'common/hooks/useI18n/useI18n.hook';
import svgIcons from 'common/svgIcons';

import { subroute } from 'routes/routes.constants';

import { memoizedComponent } from 'utils/memoizeComponent';

import { AuthProtectedLink } from '../AuthProtectedLink/AuthProtectedLink.component';
import SvgIconUrl from '../SvgIconUrl/SvgIconUrl.component';
import { SidebarItemType } from './types';

export const SidebarLink: React.FC<React.PropsWithChildren<SidebarItemType>> = memoizedComponent(
  ({
    path,
    icon,
    iconActive,
    label = '',
    external,
    tag,
    counter,
    feature,
    getNotificationState = () => new Promise((resolve) => resolve(false)),
    highlighted,
    hide = false,
    ...rest
  }) => {
    const { translate } = useI18n();
    const { isMobile } = useBowser();
    const { pathname } = useLocation();
    const { checkFeaturesAvailability } = useCustomer();
    const isAuthenticated = useSelector((state: DefaultRootState) => state.common.user.isAuthenticated);
    const isActive = pathname === path;

    const [showNotificationIcon, setShowNotificationIcon] = useState(false);

    useEffect(() => {
      if (isAuthenticated) {
        getNotificationState().then((res) => {
          setShowNotificationIcon(res);
        });
      }
    }, [getNotificationState, pathname, isAuthenticated]);

    const linkClassNames = classNames({
      sidebar__item: true,
      'sidebar__item--highlight': highlighted,
      [`sidebar__item-${label}`]: true,
    });

    const labelClassNames = classNames({
      sidebar__label: true,
      'sidebar__label--notification': showNotificationIcon,
    });

    const sidebarIconClassNames = classNames({
      sidebar__icon: true,
      [`sidebar__icon--${label}`]: true,
    });

    const ExternalLink = useCallback(
      ({ ...props }: NavLinkProps) => (
        <a
          {...props}
          {...rest}
          href={path}
          className={typeof props.className === 'string' ? props.className : undefined}
          style={typeof props.style !== 'function' ? props.style : undefined}
        >
          {props.children}
        </a>
      ),
      [path, rest],
    );

    const linkConfig: NavLinkProps = useMemo(
      () => ({
        to: path,
        ...rest,
      }),
      [path, rest],
    );

    const isFeatureEnabled = useMemo(() => {
      if (feature === undefined) return true;

      return checkFeaturesAvailability(feature);
    }, [checkFeaturesAvailability, feature]);

    const isExternal = useMemo(() => !path.includes(subroute) || external, [path, external]);

    if (isExternal) {
      linkConfig.component = ExternalLink;
    }

    const iconComponent = useMemo(() => {
      if (isMobile || isActive) {
        return <SvgIconUrl className={sidebarIconClassNames} symbol={iconActive} />;
      }
      return <SvgIconUrl className='sidebar__icon' symbol={icon} />;
    }, [icon, iconActive, isActive, isMobile, sidebarIconClassNames]);

    const labelComponent = useMemo(() => {
      if (!label) return null;

      return translate(`mobile.sidebar.label.${label}`);
    }, [label, translate]);

    const tagComponent = useMemo(() => {
      if (!tag) return null;

      return <span className='sidebar__label-tag'>{tag(translate)}</span>;
    }, [tag, translate]);

    const counterComponent = useMemo(() => {
      if (!counter) return null;

      return <span>{counter(translate)}</span>;
    }, [counter, translate]);

    const sidebarCounterClassNames = classNames({
      'Body4_small_reg_12 sidebar__counter': true,
      [`sidebar__counter--${label}`]: label,
    });

    if (hide || !isFeatureEnabled) return null;

    return (
      <AuthProtectedLink
        {...linkConfig}
        data-test={`sidebar__item-${label}`}
        className={linkClassNames}
        activeClassName='sidebar__item--active'
      >
        {iconComponent}
        <div className='sidebar__text'>
          <p data-test={`sidebar__item-label-${label}`} className={labelClassNames}>
            {labelComponent} {tagComponent}
          </p>
          <p className={sidebarCounterClassNames} data-test={`sidebar__item-counter-${label}`}>
            {counterComponent}
          </p>
          {external && !isMobile && <SvgIconUrl className='sidebar__external' symbol={svgIcons.LinkExternal()} />}
        </div>
      </AuthProtectedLink>
    );
  },
);
